import { Component, Input, OnInit } from "@angular/core";
import { PopoverController } from "@ionic/angular";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subscription, combineLatest, interval } from "rxjs";
import { distinctUntilChanged, map, startWith, take } from "rxjs/operators";
import { UtilityService } from "source/app/configuration/services/utility.service";
import { RideOpsUiService } from "source/app/configuration/services/rideops-ui.service";
import { UiQuery } from "source/app/configuration/state/ui.query";
import { TimeSpanModel } from "source/app/models/time-span.model";
import { ActionsService } from "../../../configuration/state/actions.service";
import { LocationStateQuery } from "../../../configuration/state/location-state.query";
import { QueueWidgetSettingsComponent } from "./queue-widget-settings.component";

@UntilDestroy()
@Component({
	selector: "app-queue-widget",
	templateUrl: "./queue-widget.component.html",
	styleUrls: ["./queue-widget.component.scss"],
})
export class QueueWidgetComponent implements OnInit {
	constructor(
		private locationStateQuery: LocationStateQuery,
		private actionsService: ActionsService,
		private popoverController: PopoverController,
		private utilityService: UtilityService,
		private uiQuery: UiQuery,
		public rideOpsUi: RideOpsUiService,
	) {}

	@Input() pane = false;

	private readonly updateCooldownSeconds = 3;
	private updateCooldownSub: Subscription | null = null;

	queueTime: number;
	queueTimeSpan: TimeSpanModel = TimeSpanModel.fromMinutes(0);
	updateDisabledRemainingSeconds = 0;

	showSettings$ = this.uiQuery.locked$.pipe(map((locked) => !locked));
	settings$ = this.locationStateQuery.rideOpsSettings$.pipe(
		map((settings) => {
			return {
				maximum: settings.maximumQueueTimeMinutes,
				stepSize: settings.queueTimeStepSizeMinutes,
				warningThreshold: settings.queueTimeUpdateWarningThreshold,
			};
		}),
	);

	private effectiveQueueUpdate$ = combineLatest([
		this.locationStateQuery.lastQueueTimeUpdate$,
		this.locationStateQuery.downtime$,
		interval(60_000).pipe(startWith(0)),
	]).pipe(
		map(([update, downtime]) => {
			if (update == null) {
				return undefined;
			}

			if (this.utilityService.isFromToday(update.lastUpdated) || downtime != null) {
				return update;
			}

			return undefined;
		}),
	);

	lastUpdated$ = this.effectiveQueueUpdate$.pipe(map((update) => update?.lastUpdated));

	lastUpdatedIsExpired$ = combineLatest([interval(1000).pipe(startWith(0)), this.lastUpdated$, this.settings$]).pipe(
		untilDestroyed(this),
		map(
			([_tick, lastUpdated, settings]) =>
				this.utilityService.getMinutesSince(lastUpdated) >= settings.warningThreshold,
		),
	);

	ngOnInit(): void {
		this.effectiveQueueUpdate$
			.pipe(
				map((update) => update?.time ?? 0),
				distinctUntilChanged(),
				untilDestroyed(this),
			)
			.subscribe((time) => {
				this.queueTime = time;
				this.queueTimeChanged();
			});
	}

	padQueuePart = (value: number): string => String(value ?? 0).padStart(2, "0");

	queueTimeChanged() {
		this.queueTimeSpan = TimeSpanModel.fromMinutes(this.queueTime ?? 0);
	}

	onQueueTimeInput(event: Event) {
		const value = (event as CustomEvent).detail?.value;
		this.queueTime = typeof value === "number" ? value : Number(value) || 0;
		this.queueTimeChanged();
	}

	submit() {
		if (this.updateDisabledRemainingSeconds > 0) {
			return;
		}

		this.actionsService.addUpdateQueueTimeAction(this.queueTime);

		if (this.rideOpsUi.isV2Active()) {
			this.startUpdateCooldown();
		}
	}

	private startUpdateCooldown = () => {
		this.updateCooldownSub?.unsubscribe();
		this.updateDisabledRemainingSeconds = this.updateCooldownSeconds;

		this.updateCooldownSub = interval(1000)
			.pipe(
				take(this.updateCooldownSeconds),
				map((counter) => this.updateCooldownSeconds - 1 - counter),
				untilDestroyed(this),
			)
			.subscribe((remainingSeconds) => {
				this.updateDisabledRemainingSeconds = remainingSeconds;
			});
	};

	async showSettings($event: Event) {
		const popover = await this.popoverController.create({
			component: QueueWidgetSettingsComponent,
			event: $event,
			translucent: true,
		});

		await popover.present();
	}
}
